import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterRenderEffect,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { FIELD_LENGTHS } from '../../shared/field-lengths';

/**
 * Begruendung eines Testlauf-Abbruchs (NEX-1315), wiederverwendbar fuer weitere
 * Stellen, die einen Lauf abbrechen (NEX-1400).
 *
 * <p>Selbst gebaut nach dem Muster von {@code test-run-handover-modal} und nicht auf
 * {@code ConfirmDialogComponent}: die Bestaetigung dort kennt kein Eingabefeld, keine
 * Fokusfalle und keine Fehlerzeile im offenen Dialog. Warum nicht {@code lib-modal},
 * steht am Uebergabe-Dialog.
 *
 * <p>Der Dialog schliesst sich nach der Bestaetigung nicht selbst — er meldet sie nur.
 * Wer ihn oeffnet, haelt den Aufruf offen, meldet ihn ueber {@link submitting} zurueck
 * und gibt eine Absage ueber {@link errorKey} hinein; die getippte Begruendung bleibt
 * dabei stehen.
 */
@Component({
  selector: 'app-test-run-abort-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [A11yModule, TranslatePipe, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './test-run-abort-dialog.component.html',
  styleUrl: './test-run-abort-dialog.component.scss',
})
export class TestRunAbortDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly visible = input.required<boolean>();
  /** Der Aufruf laeuft — der Dialog bleibt stehen und sperrt seine Aktionen. */
  readonly submitting = input<boolean>(false);
  /** Ob hinter dem Dialog Eingaben stehen, die der Abbruch verwirft. */
  readonly unsavedInput = input<boolean>(false);
  /** i18n-Schluessel der Absage; {@code null}, solange es keine gibt. */
  readonly errorKey = input<string | null>(null);

  readonly confirmed = output<string>();
  readonly cancelled = output<void>();

  readonly fieldLengths = FIELD_LENGTHS;

  readonly reason = signal('');

  readonly tooLong = computed(() => this.reason().length > FIELD_LENGTHS.ABORT_REASON_MAX);

  /** Nur Leerzeichen zaehlen als leer — das Backend weist sie mit {@code @NotBlank} ab. */
  readonly canSubmit = computed(() => this.reason().trim().length > 0 && !this.tooLong() && !this.submitting());

  private triggerElement: HTMLElement | null = null;

  private readonly errorMessage = viewChild<ElementRef<HTMLElement>>('errorMessage');

  /**
   * Wie beim Uebergabe-Dialog: die Seite dahinter scrollt nicht mit, der Fokus kehrt
   * zum ausloesenden Knopf zurueck, und ein erneutes Oeffnen faengt leer an.
   */
  constructor() {
    let wasVisible = false;
    effect(() => {
      const open = this.visible();
      if (open && !wasVisible) {
        this.triggerElement = document.activeElement as HTMLElement | null;
        document.body.style.overflow = 'hidden';
        this.reason.set('');
      } else if (!open && wasVisible) {
        document.body.style.overflow = '';
        this.triggerElement?.focus();
        this.triggerElement = null;
      }
      wasVisible = open;
    });
    this.destroyRef.onDestroy(() => (document.body.style.overflow = ''));
    // Beim Absenden sind die Controls gesperrt und der Fokus faellt auf `body` — eine
    // Absage holt ihn auf sich, damit er im Dialog bleibt.
    afterRenderEffect(() => {
      if (this.errorKey()) this.errorMessage()?.nativeElement.focus();
    });
  }

  submit(): void {
    if (!this.canSubmit()) return;
    this.confirmed.emit(this.reason().trim());
  }

  cancel(): void {
    if (this.submitting()) return;
    this.cancelled.emit();
  }
}
